import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import CategorySelect from './CategorySelect';
import { Analytics } from '../utils/analytics';

interface MerchantMapping {
  id: string;
  merchantName: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

const MerchantManager: React.FC = () => {
  const [mappings, setMappings] = useState<MerchantMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [newMerchant, setNewMerchant] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editMerchant, setEditMerchant] = useState('');
  const [editCategory, setEditCategory] = useState('');

  useEffect(() => {
    loadMappings();
  }, []);

  const loadMappings = async () => {
    try {
      const response = await api.get('/merchants');
      if (response.data) {
        setMappings(response.data);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Händler-Zuordnungen:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newMerchant.trim() || !newCategory) {
      alert('Bitte Händlername und Kategorie eingeben');
      return;
    }

    try {
      await api.post('/merchants', {
        merchantName: newMerchant.trim(),
        category: newCategory
      });
      Analytics.merchantMappingCreated(newCategory);
      setNewMerchant('');
      setNewCategory('');
      loadMappings();
    } catch (error) {
      console.error('Fehler beim Hinzufügen:', error);
      alert('Fehler beim Hinzufügen der Händler-Zuordnung');
    }
  };

  const handleEdit = (mapping: MerchantMapping) => {
    setEditingId(mapping.id);
    setEditMerchant(mapping.merchantName);
    setEditCategory(mapping.category);
  };

  const handleUpdate = async (id: string) => {
    if (!editMerchant.trim() || !editCategory) {
      alert('Bitte Händlername und Kategorie eingeben');
      return;
    }

    try {
      await api.put(`/merchants/${id}`, {
        merchantName: editMerchant.trim(),
        category: editCategory
      });
      setEditingId(null);
      setEditMerchant('');
      setEditCategory('');
      loadMappings();
    } catch (error) {
      console.error('Fehler beim Aktualisieren:', error);
      alert('Fehler beim Aktualisieren der Händler-Zuordnung');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Händler-Zuordnung wirklich löschen?')) return;

    try {
      await api.delete(`/merchants/${id}`);
      loadMappings();
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      alert('Fehler beim Löschen der Händler-Zuordnung');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditMerchant('');
    setEditCategory('');
  };

  if (loading) {
    return <div>Lade Händler-Zuordnungen...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <h2>Händler-Zuordnungen</h2>
      <p style={{ color: '#64748b', marginBottom: '20px' }}>
        Lege fest, welche Kategorie beim Scannen automatisch für bestimmte Händler vorgeschlagen wird.
      </p>

      {/* Neue Zuordnung hinzufügen */}
      <div style={{ 
        marginBottom: '30px', 
        padding: '20px', 
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{ marginTop: 0 }}>Neue Zuordnung</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
              Händler (z.B. REWE, EDEKA)
            </label>
            <input
              type="text"
              value={newMerchant}
              onChange={(e) => setNewMerchant(e.target.value)}
              placeholder="Händlername"
              style={{ width: '100%', padding: '8px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px' }}>
              Kategorie
            </label>
            <CategorySelect
              value={newCategory}
              onChange={setNewCategory}
            />
          </div>
          <button
            onClick={handleAdd}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Hinzufügen
          </button>
        </div>
      </div>

      {/* Liste der Zuordnungen */}
      {mappings.length === 0 ? (
        <p style={{ color: '#94a3b8', textAlign: 'center', padding: '40px' }}>
          Noch keine Händler-Zuordnungen vorhanden.<br/>
          Füge deine erste Zuordnung hinzu!
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f1f5f9' }}>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Händler</th>
              <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e2e8f0' }}>Kategorie</th>
              <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e2e8f0' }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {mappings.map((mapping) => (
              <tr key={mapping.id} style={{ borderBottom: '1px solid #e2e8f0' }}>
                {editingId === mapping.id ? (
                  <>
                    <td style={{ padding: '12px' }}>
                      <input
                        type="text"
                        value={editMerchant}
                        onChange={(e) => setEditMerchant(e.target.value)}
                        style={{ width: '100%', padding: '6px' }}
                      />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <CategorySelect
                        value={editCategory}
                        onChange={setEditCategory}
                      />
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <button
                        onClick={() => handleUpdate(mapping.id)}
                        style={{
                          padding: '6px 12px',
                          marginRight: '8px',
                          backgroundColor: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Speichern
                      </button>
                      <button
                        onClick={handleCancel}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#6b7280',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Abbrechen
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ padding: '12px', fontWeight: 500 }}>{mapping.merchantName}</td>
                    <td style={{ padding: '12px' }}>
                      <CategorySelect
                        value={mapping.category}
                        onChange={() => {}}
                        disabled
                      />
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}>
                      <button
                        onClick={() => handleEdit(mapping)}
                        style={{
                          padding: '6px 12px',
                          marginRight: '8px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => handleDelete(mapping.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Löschen
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ 
        marginTop: '20px', 
        padding: '15px', 
        backgroundColor: '#eff6ff',
        borderRadius: '6px',
        border: '1px solid #bfdbfe'
      }}>
        <strong>💡 Tipp:</strong> Der Händlername wird automatisch in Großbuchstaben gespeichert und beim OCR-Scan verglichen.
      </div>
    </div>
  );
};

export default MerchantManager;
